import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { RevealLine, FadeUp } from "../components/Reveal";

const inputCls = "w-full bg-white border border-rule rounded-sm px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-green/40 focus:border-green transition-colors duration-200";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft mb-2";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

export default function EsopTool() {
  const [valuation, setValuation] = useState(50);
  const [shares, setShares] = useState(100000);
  const [grant, setGrant] = useState(1000);
  const [strike, setStrike] = useState(100);
  const [years, setYears] = useState(4);
  const [cliff, setCliff] = useState(1);

  const pricePerShare = (valuation * 1e7) / shares;
  const grantValue = grant * pricePerShare;
  const exerciseCost = grant * strike;
  const netValue = grantValue - exerciseCost;
  const ownership = (grant / shares) * 100;

  const schedule = [];
  for (let y = 1; y <= years; y++) {
    const pct = y === 1 ? (cliff / 1) * (100 / years) : 100 / years;
    schedule.push({ year: y, vested: Math.min(100, (100 / years) * y), shares: Math.round(grant * Math.min(1, y / years)) });
  }

  return (
    <main className="pt-32 pb-16" data-testid="esop-tool-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <FadeUp y={12}>
          <Link to="/#resources" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:text-green transition-colors duration-200 mb-10" data-testid="tool-back-link">
            <ArrowLeft size={13} /> All resources
          </Link>
        </FadeUp>
        <p className="font-mono text-lg uppercase tracking-[0.25em] text-ink-mid mb-5">Interactive toolkit · Free</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-ink mb-6">
          <RevealLine animateOnLoad>ESOP <span className="italic text-green">Sutra Toolkit.</span></RevealLine>
        </h1>
        <FadeUp className="max-w-2xl text-ink-mid leading-relaxed mb-16">
          A plain-English way to understand what an ESOP grant is worth. Enter your company's numbers and see option value, ownership and a vesting schedule — without a lawyer.
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <FadeUp className="lg:col-span-5 bg-white rounded-2xl p-8 space-y-6">
            <div>
              <label className={labelCls}>Company valuation (₹ Cr)</label>
              <input type="number" min="1" value={valuation} onChange={(e) => setValuation(+e.target.value || 0)} className={inputCls} data-testid="esop-valuation-input" />
            </div>
            <div>
              <label className={labelCls}>Total shares outstanding</label>
              <input type="number" min="1" value={shares} onChange={(e) => setShares(+e.target.value || 1)} className={inputCls} data-testid="esop-shares-input" />
            </div>
            <div>
              <label className={labelCls}>Your grant (options)</label>
              <input type="number" min="0" value={grant} onChange={(e) => setGrant(+e.target.value || 0)} className={inputCls} data-testid="esop-grant-input" />
            </div>
            <div>
              <label className={labelCls}>Exercise price per option (₹)</label>
              <input type="number" min="0" value={strike} onChange={(e) => setStrike(+e.target.value || 0)} className={inputCls} data-testid="esop-strike-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Vesting (years)</label>
                <input type="number" min="1" max="6" value={years} onChange={(e) => setYears(Math.max(1, Math.min(6, +e.target.value || 4)))} className={inputCls} data-testid="esop-years-input" />
              </div>
              <div>
                <label className={labelCls}>Cliff (years)</label>
                <input type="number" min="0" max="2" value={cliff} onChange={(e) => setCliff(Math.max(0, Math.min(2, +e.target.value || 1)))} className={inputCls} data-testid="esop-cliff-input" />
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.1} className="lg:col-span-7 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {[
                ["Value per share", inr(pricePerShare)],
                ["Your ownership", `${ownership.toFixed(3)}%`],
                ["Grant value today", inr(grantValue)],
                ["Net gain after exercise", inr(netValue)],
              ].map(([k, v]) => (
                <div key={k} className="bg-white rounded-2xl p-7" data-testid={`esop-output-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft mb-2">{k}</p>
                  <p className="font-serif text-2xl lg:text-3xl text-green">{v}</p>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft mb-6">Vesting schedule · {years} years · {cliff}-year cliff</p>
              <div className="space-y-3">
                {schedule.map((s) => (
                  <div key={s.year} className="flex items-center gap-4" data-testid={`esop-vest-year-${s.year}`}>
                    <span className="font-mono text-xs text-ink-mid w-14">Year {s.year}</span>
                    <div className="flex-1 h-2 bg-green-lt rounded-full overflow-hidden">
                      <div className="h-full bg-green rounded-full transition-[width] duration-500" style={{ width: `${s.vested}%` }} />
                    </div>
                    <span className="font-mono text-xs text-ink w-28 text-right">{s.shares.toLocaleString("en-IN")} opts · {s.vested}%</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-ink-soft leading-relaxed">
                Nothing vests before the {cliff}-year cliff. At the cliff, {cliff > 0 ? `${(100 / years) * cliff}% vests at once` : "vesting begins"}, then the rest vests evenly. Tax is typically due at exercise (perquisite) and at sale (capital gains) — talk to us before you sign.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </main>
  );
}
